import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "redux/hooks";
import { Box, Button, Card, Stack, Typography, alpha } from "@mui/material";
import { verifyPwd } from "../OnboardingFunctions";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { RouterLink } from "components/router-link";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import {
  mobile,
  signer,
  signerAccentBar,
  signerDoneGradient,
} from "theme/colors";
import { authRef, refFont } from "screens/Auth/AuthReferenceStyles";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

const VerifyPassword = () => {
  useTitle(title.VERIFY_PASSWORD);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const context = new URLSearchParams(location.search).get("context");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>("");

  const [countdown, setCountdown] = useState<number>(7);
  useEffect(() => {
    verifyPwd(context!, setLoading, dispatch, setError);
  }, []);

  useEffect(() => {
    if (!loading && !error) {
      if (countdown > 0) {
        const timerId = setTimeout(() => setCountdown(countdown - 1), 1000);
        return () => clearTimeout(timerId);
      } else {
        history.push(viewpaths.loginViewPath);
      }
    }
  }, [loading, error, countdown]);

  const progressValue = ((7 - countdown) / 7) * 100;

  return (
    <Box
      component="main"
      id="main-content"
      tabIndex={-1}
      sx={{
        minHeight: "100vh",
        backgroundColor: authRef.pageBg,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 5,
        boxSizing: "border-box",
        outline: "none",
      }}
    >
      {/* Brand accent bar across the top of the canvas, per reference 3b. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "5px",
          background: signerAccentBar,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      />

      <Card
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 480,
          borderRadius: "16px",
          border: `1px solid ${authRef.cardBorder}`,
          boxShadow: authRef.cardShadow,
          padding: { xs: "32px 24px 28px", sm: "44px 48px 38px" },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        {error ? (
          <Stack spacing={3} alignItems="center" width="100%">
            <Box
              component="img"
              src={inkfreeLogo}
              alt="Inkfree e-Sign"
              sx={{ width: 106, height: "auto", display: "block" }}
            />
            <Typography
              component="h1"
              sx={{
                color: "error.main",
                fontSize: refFont.cardHeadingLarge,
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              Oops! {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              component={RouterLink}
              href={viewpaths.loginViewPath}
              sx={{
                height: "48px",
                borderRadius: "8px",
                fontWeight: 700,
                textTransform: "none",
                fontSize: refFont.cardButton,
              }}
            >
              Go to login
            </Button>
          </Stack>
        ) : (
          <>
            <Box
              component="img"
              src={inkfreeLogo}
              alt="Inkfree e-Sign"
              sx={{ width: 106, height: "auto", display: "block" }}
            />

            {/* Success badge */}
            <Box
              aria-hidden="true"
              sx={{
                width: 72,
                height: 72,
                mt: 3.5,
                borderRadius: "50%",
                background: signerDoneGradient.signed,
                boxShadow: `0 10px 28px ${alpha(signer.gradient.coral, 0.28)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "common.white",
              }}
            >
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.5 12.5l5 5L20 6.5" />
              </svg>
            </Box>

            <Typography
              component="h1"
              sx={{
                color: mobile.base[500],
                fontSize: refFont.cardHeadingLarge,
                fontWeight: 800,
                textAlign: "center",
                mt: 2.5,
              }}
            >
              You're officially Inkfree!
            </Typography>
            <Typography
              sx={{
                color: authRef.subText,
                fontSize: refFont.body,
                lineHeight: 1.6,
                textAlign: "center",
                mt: 1.25,
              }}
            >
              Your account has been activated.
              <br />
              Time to send your first sign request.
            </Typography>

            {/* Countdown */}
            <Box
              sx={{
                width: "100%",
                mt: 3.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(progressValue)}
                aria-label="Time until redirect to log in"
                sx={{
                  height: 6,
                  borderRadius: "999px",
                  backgroundColor: signer.border.light,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${progressValue}%`,
                    height: "100%",
                    borderRadius: "999px",
                    background: signerAccentBar,
                    transition: "width 1s linear",
                  }}
                />
              </Box>
              <Typography
                aria-live="polite"
                sx={{
                  fontSize: refFont.link,
                  color: authRef.metaText,
                  textAlign: "center",
                }}
              >
                Taking you to log in in{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 700, color: mobile.base[500] }}
                >
                  {countdown} seconds
                </Box>{" "}
                — or skip the wait:
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              component={RouterLink}
              href={viewpaths.loginViewPath}
              sx={{
                mt: 2,
                height: "48px",
                borderRadius: "8px",
                fontWeight: 700,
                textTransform: "none",
                fontSize: refFont.cardButton,
                letterSpacing: 0,
              }}
            >
              Go to log in now
            </Button>
          </>
        )}
      </Card>

      <Typography
        sx={{
          mt: 2.5,
          fontSize: refFont.cardFooter,
          color: authRef.footerText,
          textAlign: "center",
        }}
      >
        © {new Date().getFullYear()} Inkfree. All rights reserved.
      </Typography>

      <CustomLoader isLoading={loading} />
    </Box>
  );
};

export default VerifyPassword;
